import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Button, Image, ThemeConfig } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import useWindowSize from '../../hooks/useWindowSize';
import DashboardMenuNavigation from './DashboardMenuNavigation';

import styles from './DashboardMenu.module.css';

export const FLOATING_MENU_WIDTH = 1024;
export const DESKTOP_MENU_WIDTH = 1312;

export type DashboardMenuTheme = NonNullable<ThemeConfig['components']>['Menu'];

export interface DashboardMenuItem {
  key: string;
  title: React.ReactNode;
  icon?: React.ReactNode;
  iconActive?: React.ReactNode;
  /** Rendered as an anchor so the item can be opened in a new tab */
  href?: string;
  badge?: React.ReactNode;
  disabled?: boolean;
  /** Footer items only: renders the link with the primary color */
  primary?: boolean;
  onClick?: () => void;
  /** Navigation items only: draws a separator above the item */
  dividerBefore?: boolean;
  /** Nested items, rendered as a SubMenu (any depth) */
  children?: DashboardMenuItem[];
}

/** Slots receive the current collapsed view state so they can adapt their layout */
type DashboardMenuSlot = React.ReactNode | ((isCollapsed: boolean) => React.ReactNode);

interface DashboardMenuProps {
  items: DashboardMenuItem[];
  selectedKey?: string;
  show: boolean;
  isCollapsed: boolean;
  onCollapseChange: (isCollapsed: boolean) => void;
  onNavigate: () => void;
  logo: string;
  logoCollapsed: string;
  onLogoClick?: () => void;
  version?: string;
  navigationTitle?: string;
  menuTheme?: DashboardMenuTheme;
  headerExtra?: DashboardMenuSlot;
  beforeNavigation?: DashboardMenuSlot;
  footerItems?: DashboardMenuItem[];
  footer?: DashboardMenuSlot;
}

const renderSlot = (slot: DashboardMenuSlot | undefined, isCollapsed: boolean) =>
  typeof slot === 'function' ? slot(isCollapsed) : slot;

const DashboardMenu: React.FC<DashboardMenuProps> = ({
  items,
  selectedKey,
  show,
  isCollapsed,
  onCollapseChange,
  onNavigate,
  logo,
  logoCollapsed,
  onLogoClick,
  version,
  navigationTitle,
  menuTheme,
  headerExtra,
  beforeNavigation,
  footerItems,
  footer,
}) => {
  const [isShown, setIsShown] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const windowSize = useWindowSize();

  const isMobileMenu = windowSize.width < FLOATING_MENU_WIDTH;
  const isCollapsedView = isCollapsed && !isHovered && (!isMobileMenu || !isShown);
  const isVisible = show || !isMobileMenu;

  useEffect(() => {
    if (show) {
      // Synchro avec un systeme externe au rendu (DOM, storage, timer, toast,
      // navigation) : l'etat accompagne un effet de bord qui ne peut pas remonter au rendu.
      // eslint-disable-next-line react/set-state-in-effect
      setIsShown(true);
    } else {
      setTimeout(() => {
        setIsShown(false);
      }, 300);
    }
  }, [show]);

  return (
    <div
      className={classNames(styles['dashboardMenu'], {
        [styles['show']]: isVisible,
        [styles['dashboardMenuCollapsed']]: isCollapsedView,
      })}
      onMouseEnter={() => {
        // Expand the collapsed menu on hover, desktop only.
        if (!isMobileMenu) setIsHovered(true);
      }}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={styles['dashboardMenuHeader']}>
        <div
          className={classNames(styles['dashboardMenuLogoCollapsed'], {
            [styles['dashboardMenuLogoClickable']]: !!onLogoClick,
          })}
          onClick={onLogoClick}
        >
          <Image width={32} src={logoCollapsed} alt="logo-collapsed" preview={false} />
        </div>

        {headerExtra && (
          <div className={styles['dashboardMenuHeaderExtra']}>
            {renderSlot(headerExtra, isCollapsedView)}
          </div>
        )}

        <div
          className={classNames(styles['dashboardMenuLogo'], {
            [styles['dashboardMenuLogoClickable']]: !!onLogoClick,
          })}
          onClick={onLogoClick}
        >
          <Image width={122} src={logo} alt="logo" preview={false} />
        </div>

        {/* Le repli sur la même ligne que l'avatar et le logo : les trois
            occupaient deux lignes pour trois éléments qui tiennent en une, et
            le logo se lit mieux centré entre ses deux boutons. Replié, la
            colonne les empile — `order` le garde en bas, voir le module CSS. */}
        <Button
          type="text"
          size="small"
          className={styles['dashboardMenuCollapseButton']}
          icon={!isCollapsed || isMobileMenu ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
          onClick={() => {
            if (isMobileMenu) {
              onNavigate();
              return;
            }
            setIsHovered(false);
            onCollapseChange(!isCollapsed);
          }}
        />
      </div>

      <div className={styles['dashboardMenuVersionContainer']}>
        {version && (
          <h5
            className={classNames(styles['dashboardMenuVersion'], {
              [styles['dashboardMenuVersionHidden']]: isCollapsedView,
            })}
          >
            v{version}
          </h5>
        )}
      </div>

      {beforeNavigation && (
        <div className={styles['dashboardMenuSection']}>
          {renderSlot(beforeNavigation, isCollapsedView)}
        </div>
      )}

      <div className={styles['dashboardMenuNavigationContainer']}>
        {navigationTitle && !isCollapsedView && (
          <span className={styles['dashboardMenuNavigationTitle']}>{navigationTitle}</span>
        )}
        <DashboardMenuNavigation
          items={items}
          selectedKey={selectedKey}
          isCollapsed={isCollapsedView}
          onNavigate={onNavigate}
          menuTheme={menuTheme}
        />
      </div>

      <div className={styles['dashboardMenuFooter']}>
        {footerItems && footerItems.length > 0 && (
          <div className={styles['dashboardMenuFooterLinks']}>
            {footerItems.map((item) => (
              <button
                key={item.key}
                type="button"
                className={classNames(styles['dashboardMenuFooterLink'], {
                  [styles['dashboardMenuFooterLinkPrimary']]: item.primary,
                  [styles['dashboardMenuFooterLinkDisabled']]: item.disabled,
                })}
                onClick={item.onClick}
                disabled={item.disabled}
              >
                <span className={styles['dashboardMenuFooterLinkIcon']}>{item.icon}</span>
                <span className={styles['dashboardMenuFooterLinkLabel']}>{item.title}</span>
                {item.badge}
              </button>
            ))}
          </div>
        )}
        {renderSlot(footer, isCollapsedView)}
      </div>
    </div>
  );
};

export default DashboardMenu;
